<template>
  <div
    class="bg-white/80 dark:bg-transparent relative flex-1 min-h-0 
    border-l-2 border-r 
    border-l-[var(--p-surface-0)]/80 
    dark:border-[var(--p-primary-color)]/30
    dark:border-r-[var(--p-primary-color)]/20
    "

  >
    <div
      ref="messagesContainer"
      class="h-full min-h-0 overscroll-contain overflow-y-auto px-3 py-3 space-y-1"
      @scroll.passive="handleScroll"
    >
      <div v-if="props.hasMoreHistory !== false" class="sticky top-0 z-10 flex justify-center py-2">
        <button
          type="button"
          class="app-card-glass bg-white/70! dark:bg-[var(--p-primary-950)]/70! backdrop-blur-sm rounded-full cursor-pointer px-3 py-1 text-xs font-medium text-[var(--p-surface-600)] shadow-sm backdrop-blur transition hover:scale-101 hover:bg-[var(--p-primary-100)]! disabled:cursor-not-allowed disabled:opacity-60 dark:border-[var(--p-surface-700)] dark:text-[var(--p-primary-300)] dark:hover:bg-[var(--p-surface-700)]/50!"
          :disabled="isLoadingHistory"
          @click="handleLoadPreviousMessages"
        >
          {{
            isLoadingHistory
              ? $t('whatsapp.message_list.loading_messages')
              : $t('whatsapp.message_list.view_previous_messages')
          }}
        </button>
      </div>

      <div
        v-for="message in messages"
        :key="message.id"
        class="flex w-full"
        :class="
          message.content.type === MessageType.INTERACTION
            ? 'justify-center'
            : isOutgoingSourceType(message.source_type)
              ? 'justify-end'
              : 'justify-start'
        "
      >
        <div
          :id="`message-${message.id}`"
          class="flex flex-col gap-0.5 transition-all duration-200"
          :class="[
            message.content.type === MessageType.INTERACTION
              ? 'items-center max-w-[85%]'
              : isOutgoingSourceType(message.source_type)
                ? 'items-end max-w-[75%]'
                : 'items-start max-w-[75%]',
          ]"
        >
          <MessageItem
            :message="message"
            :source-type="message.source_type"
            :translation-enabled="translationEnabled"
            :translation-language="translationLanguage"
            :translation-loading="(translationLoadingMessageIds ?? []).includes(message.id)"
            @reply="$emit('reply', $event)"
            @delete="$emit('delete', $event)"
            @retry-translation="$emit('retry-translation', $event)"
          />
        </div>
      </div>
    </div>

    <button
      v-if="showScrollToBottom"
      type="button"
      class="absolute cursor-pointer bottom-4 right-4 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-[var(--p-surface-200)] bg-[var(--p-surface-0)] text-[var(--p-primary-color)] shadow-lg shadow-[var(--p-surface-950)]/15 transition hover:bg-[var(--p-surface-100)] active:scale-95 dark:border-[var(--p-surface-700)] dark:bg-[var(--p-surface-800)] dark:text-[var(--p-primary-100)] dark:shadow-black/30 dark:hover:bg-[var(--p-surface-700)]"
      aria-label="Ir al ultimo mensaje"
      title="Ir al ultimo mensaje"
      @click="handleScrollToBottom"
    >
      <ChevronDown :size="22" :stroke-width="2.5" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, provide, nextTick } from 'vue'
import { ChevronDown } from 'lucide-vue-next'
import type { Message } from '../../types/interfaces'
import { MessageType, MessageSourceType } from '../../types/enums'
import MessageItem from './MessageItem.vue'
import { useChatMessages } from '../../composables/useChatMessages'
import { buildChatMediaItems, chatMediaItemsKey } from '../../composables/useChatMediaViewer'

interface Props {
  messages: Message[]
  isLoadingHistory?: boolean
  hasMoreHistory?: boolean
  translationEnabled?: boolean
  translationLanguage?: string
  translationLoadingMessageIds?: string[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  reply: [message: Message]
  delete: [message: Message]
  loadHistory: []
  'retry-translation': [message: Message]
  'translation-pivot': [messageId: string]
}>()

provide(
  'messages',
  computed(() => props.messages),
)

const chatMediaItems = computed(() => buildChatMediaItems(props.messages))

provide(chatMediaItemsKey, chatMediaItems)

const messagesContainer = ref<HTMLDivElement>()
const showScrollToBottom = ref(false)
const SCROLL_TO_BOTTOM_THRESHOLD = 260

const { scrollToBottom } = useChatMessages(messagesContainer)

interface ScrollState {
  scrollHeight: number
  scrollTop: number
  firstMessageId?: string
}

const previousScrollState = ref<ScrollState | null>(null)

const isOutgoingSourceType = (sourceType: string) => {
  return sourceType === MessageSourceType.SALIENTE || sourceType === MessageSourceType.TEMPLATE
}

const handleLoadPreviousMessages = () => {
  if (props.isLoadingHistory || props.hasMoreHistory === false) return

  captureScrollState()

  emit('loadHistory')
}

const handleScroll = () => {
  if (!messagesContainer.value) return
  updateScrollToBottomVisibility()
  emitTranslationPivot()
}

const updateScrollToBottomVisibility = () => {
  const container = messagesContainer.value
  if (!container) return

  const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
  showScrollToBottom.value = distanceFromBottom > SCROLL_TO_BOTTOM_THRESHOLD
}

const handleScrollToBottom = () => {
  scrollToBottom()
  nextTick(() => {
    updateScrollToBottomVisibility()
    emitTranslationPivot()
  })
}

const captureScrollState = () => {
  previousScrollState.value = {
    scrollHeight: messagesContainer.value?.scrollHeight ?? 0,
    scrollTop: messagesContainer.value?.scrollTop ?? 0,
    firstMessageId: props.messages[0]?.id,
  }
}

const isTranslationCandidate = (message: Message) => {
  if (!props.translationEnabled) return false
  if (message.content.type !== MessageType.TEXT) return false
  const translation = message.content.ai_translate?.[props.translationLanguage || 'en']
  return translation?.status !== 'ready' && translation?.status !== 'error'
}

const emitTranslationPivot = () => {
  const container = messagesContainer.value
  if (!container || !props.translationEnabled) return

  const containerRect = container.getBoundingClientRect()
  const viewportCenter = containerRect.top + containerRect.height / 2
  let pivot: { id: string; distance: number } | null = null

  for (const message of props.messages) {
    if (!isTranslationCandidate(message)) continue
    if (props.translationLoadingMessageIds?.includes(message.id)) continue

    const element = document.getElementById(`message-${message.id}`)
    if (!element) continue

    const rect = element.getBoundingClientRect()
    if (rect.bottom < containerRect.top || rect.top > containerRect.bottom) continue

    const distance = Math.abs(rect.top + rect.height / 2 - viewportCenter)
    if (!pivot || distance < pivot.distance) {
      pivot = { id: message.id, distance }
    }
  }

  if (pivot) {
    emit('translation-pivot', pivot.id)
  }
}

const restoreScrollAfterHistoryLoad = (previous: ScrollState) => {
  const container = messagesContainer.value
  if (!container) return

  const heightDifference = container.scrollHeight - previous.scrollHeight
  container.scrollTop = previous.scrollTop + heightDifference
}

watch(
  () => props.messages[0]?.id,
  (firstMessageId) => {
    const previous = previousScrollState.value
    if (!previous || firstMessageId === previous.firstMessageId) return

    // Se consume aqui mismo para que el watch de abajo no lo tome por un ancla
    // que nadie uso.
    previousScrollState.value = null
    nextTick(() => restoreScrollAfterHistoryLoad(previous))
  },
  { flush: 'post' },
)

// La ultima pagina puede volver vacia y entonces no hay nada que reponer: sin esto el
// ancla sobreviviria y daria un salto al cambiar de chat, que tambien mueve el primer
// mensaje.
watch(
  () => props.isLoadingHistory,
  (isLoading, wasLoading) => {
    if (wasLoading && !isLoading) previousScrollState.value = null
  },
  { flush: 'post' },
)

// Scroll to bottom when the last message id changes (new message sent or received).
// flush: 'post' gives the correct old/new values unlike onBeforeUpdate which already
// sees the updated props when it fires.
watch(
  () => props.messages[props.messages.length - 1]?.id,
  (newId, oldId) => {
    if (!oldId || !newId || newId === oldId) return
    scrollToBottom()
    nextTick(() => {
      updateScrollToBottomVisibility()
      emitTranslationPivot()
    })
  },
  { flush: 'post' },
)

// Initial load: scroll to bottom when messages first populate.
watch(
  () => props.messages[props.messages.length - 1]?.id,
  (lastMessageId, previousLastMessageId) => {
    if (!lastMessageId) return
    if (lastMessageId === previousLastMessageId) return
    if (previousLastMessageId) return

    nextTick(() => {
      scrollToBottom()
      updateScrollToBottomVisibility()
      emitTranslationPivot()
    })
  },
  { immediate: true },
)

watch(
  () => [props.translationEnabled, props.translationLanguage, props.messages.length],
  () => {
    nextTick(() => {
      updateScrollToBottomVisibility()
      emitTranslationPivot()
    })
  },
  { immediate: true, flush: 'post' },
)

defineExpose({
  messagesContainer,
  scrollToBottom,
})
</script>

<style>
.highlight-message {
  animation: highlight-bubble 2.5s ease-in-out;
}

@keyframes highlight-bubble {
  0% {
    filter: brightness(1);
  }

  20% {
    filter: brightness(1);
  }

  40% {
    filter: brightness(0.7);
  }

  60% {
    filter: brightness(1);
  }

  80% {
    filter: brightness(0.7);
  }

  100% {
    filter: brightness(1);
  }
}
</style>
